/**
 * LeadFunnelBar — 4-stage horizontal progress: Contacted → Scheduled → Assessment → Signed.
 *
 * Receives `funnel` payload from /api/coach/leads/{id}:
 *   { contacted: bool, scheduled: bool, assessed: bool, signed: bool, current_stage: string|null }
 *
 * Each stage is a numbered circle + label, connected by a line. Completed stages
 * pulse the brand-red accent; the current/next stage shows the empty ring outlined
 * in red; future stages are dimmed.
 */
import { CheckCircle2 } from "lucide-react";

const STAGES = [
    { key: "contacted", label: "Contacted" },
    { key: "scheduled", label: "Scheduled" },
    { key: "assessed",  label: "Assessment" },
    { key: "signed",    label: "Signed" },
];

export default function LeadFunnelBar({ funnel, onStageClick }) {
    if (!funnel) return null;
    const stages = STAGES.map((s) => ({ ...s, done: !!funnel[s.key] }));
    // The "current" stage is the FIRST not-done stage (i.e. the next step to take).
    const nextIdx = stages.findIndex((s) => !s.done);
    const allDone = nextIdx === -1;

    return (
        <div data-testid="lead-funnel-bar" className="w-full">
            <div className="flex items-center justify-between">
                {stages.map((s, i) => {
                    const isNext = !allDone && i === nextIdx;
                    const isDone = s.done;
                    const clickable = !!onStageClick;
                    const circleStyle = isDone
                        ? "bg-brand-red border-brand-red text-white"
                        : isNext
                            ? "border-brand-red text-brand-red bg-brand-red/10"
                            : "border-zinc-700 text-zinc-500 bg-zinc-950";
                    const labelStyle = isDone
                        ? "text-white"
                        : isNext
                            ? "text-brand-red"
                            : "text-zinc-500";
                    const hoverStyle = clickable
                        ? "cursor-pointer hover:scale-[1.04] focus:outline-none focus:ring-2 focus:ring-brand-red/40 rounded-full"
                        : "";
                    const Inner = (
                        <div className="flex flex-col items-center text-center flex-shrink-0 min-w-0">
                            <div
                                className={`relative w-9 h-9 rounded-full border-2 flex items-center justify-center text-xs font-display transition-transform ${circleStyle} ${hoverStyle}`}
                            >
                                {isDone ? <CheckCircle2 className="w-4 h-4" /> : <span>{i + 1}</span>}
                            </div>
                            <div className={`mt-2 text-[10px] tracking-[0.2em] uppercase ${labelStyle} whitespace-nowrap`}>
                                {s.label}
                            </div>
                        </div>
                    );
                    return (
                        <div
                            key={s.key}
                            data-testid={`funnel-stage-${s.key}`}
                            data-done={isDone}
                            data-current={isNext}
                            className="flex-1 flex items-center"
                        >
                            {clickable ? (
                                <button
                                    type="button"
                                    data-testid={`funnel-stage-${s.key}-btn`}
                                    onClick={() => onStageClick(s.key, { done: isDone })}
                                    className="flex-shrink-0 text-left"
                                    title={isDone ? `Re-open ${s.label.toLowerCase()}` : `Mark ${s.label.toLowerCase()} as done`}
                                >
                                    {Inner}
                                </button>
                            ) : (
                                Inner
                            )}
                            {i < stages.length - 1 && (
                                <div
                                    className={`flex-1 h-0.5 mx-2 mt-[-22px] transition-colors ${
                                        s.done ? "bg-brand-red" : "bg-zinc-800"
                                    }`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            {allDone && (
                <div data-testid="funnel-complete" className="mt-3 text-center text-[10px] tracking-[0.25em] uppercase text-emerald-400">
                    Funnel complete — they're in.
                </div>
            )}
        </div>
    );
}
